import React, {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';
import classNames from 'classnames';
import {
  Popover,
  PopoverProps,
  PopoverMode,
  PopoverWidthMode,
} from 'js/design_system/Popover';
import { useDebounce } from 'js/social/shared/useDebounce';
import { scrollChildIntoView } from 'js/social/shared/utils';
import { useRandomId } from 'js/utils/hooks/useRandomId';
import { Errors } from 'js/design_system/forms/Errors';
import { usePopoverFocus, mod } from 'js/design_system/forms/utils';

import 'app/assets/stylesheets/design_system/form.scss';
import { t } from 'js/intl/t';
import 'js/i18n/page_translations/design_system/en.wjs';

// Because the Popover uses React.createPortal, we need to use this to ensure that
// DS2.5 styles are applied to the menu.
const DropdownMenuStyleContext = styled.div``;

const defaultEmpty = () => {
  return (
    <div className="ds2-5-typeahead--message">
      {t('design_system.typeahead.no_results')}
    </div>
  );
};

const defaultLoading = () => {
  return (
    <div className="ds2-5-typeahead--message">
      {t('design_system.typeahead.searching')}
    </div>
  );
};

const defaultRenderValue = (value: unknown) => {
  return value ? String(value) : '';
};

const ListItem = styled.button<{ selected?: boolean }>`
  &.selected {
    background-color: #afccfc;
  }
`;

export type TypeaheadProps<T> = {
  /** Optional value to show as selected */
  value?: T;
  /** The input's label. Shown at the top of the component. */
  label?: ReactNode;
  /** The input's description. Shown directly underneath the label. */
  description?: ReactNode;
  /** The placeholder for the underlying input */
  placeholder?: string;
  /** Content to prepend before the actual text input */
  prepend?: ReactNode;
  /** Content to append after the actual text input */
  append?: ReactNode;
  /** The errors to show beneath the input */
  errors?: ReactNode[];
  /** Whether to decorate the input with error styling */
  hasError?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Delay to use when debouncing search input */
  debounceDelay?: number;
  /** Get a key from a given option (for React) */
  keyExtractor?: (option: T) => React.Key;
  /** Render callback for a single option in the typeahead dropdpown */
  renderOption: (option: T) => ReactNode;
  /** Render callback when no results are available */
  renderEmpty?: (search: string) => ReactNode;
  /** Hides the popover when there are no results */
  hideEmpty?: boolean;
  /** Render callback when loading results */
  renderLoading?: (search: string) => ReactNode;
  /** Keeps the current results rendered until new results are fetched */
  seamlessLoading?: boolean;
  /** Callback to invoke when the search value changes (likely asynchronous) */
  onSearch: (search: string) => T[] | Promise<T[]>;
  /** Callback to invoke when the input value changes */
  onInputChange?: (input: string) => void;
  /** Callback to invoke when selecting an option */
  onSelect: (option: T) => void;
  /** Props for the internal popover element */
  popoverProps?: Partial<PopoverProps>;
  /** Render callback for the value */
  renderValue?: (value?: T) => string;
  /** Function to create a new option from search text */
  onCreate?: (search: string) => T;
  /** Toggles weather or not a user created option appears in the option list */
  showCreatedOption?: boolean;
  /** Updates the value in the search box to what's been selected */
  updateSearchTextOnSelect?: boolean;
  /** Toggles whether the scrollbar is always visible */
  alwaysShowScrollbar?: boolean;
  /** Additional class name to assing to the input's container */
  className?: string;
  /** Styles for the dropdown container.  */
  dropdownContainerStyle?: React.CSSProperties;
  /** Styles for the dropdown item.  */
  dropdownItemStyle?: React.CSSProperties;
};

/**
 * # Typeahead
 * A typeahead input for searching and selecting from a list of options. Supports labels, descriptions, and error messages.
 *
 * Props:
 * - `value`: Optional value to show as selected
 * - `label`: The input's label. Shown at the top of the component.
 * - `description`: The input's description. Shown directly underneath the label.
 * - `placeholder`: The placeholder for the underlying input
 * - `prepend`: Content to prepend before the actual text input
 * - `append`: Content to append after the actual text input
 * - `errors`: The errors to show beneath the input
 * - `hasError`: Whether to decorate the input with error styling
 * - `debounceDelay`: Delay (in ms) to use when debouncing search input
 * - `disabled`: Whether the input is disabled
 * - `keyExtractor`: Get a key from a given option (for React)
 * - `renderOption`: Render callback for how to display a single option in the dropdown
 * - `renderEmpty`: Render callback when no results are available
 * - `hideEmpty`: Hides the popover component entirely when no results are available
 * - `renderLoading`: Render callback when loading results
 * - `seamlessLoading`: Keeps the current results rendered until new results are fetched
 * - `onSearch`: Callback to invoke when the search value changes (likely asynchronous)
 * - `onInputChange`: Callback to invoke when the input value changes
 * - `onSelect`: Callback to invoke when selecting an option
 * - `popoverProps`: Props for the internal popover element
 * - `renderValue`: Render callback for the value
 * - `onCreate`: Function to create a new option from search text
 * - `showCreatedOption`: Toggles weather or not a user created option appears in the option list
 * - `updateSearchTextOnSelect`: Updates the value in the search box to what's been selected
 * - `className`: Additional class name to assing to the input's container
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Typeahead } from 'js/design_system/forms/Typeahead';
 *
 * const [ris, setRis] = useState<string[]>([]);
 *
 * <Typeahead<string>
 *  label="Research Interests"
 *  renderOption={(ri) => ri}
 *  onSearch={(search) => searchRis(search, ris)}
 *  onSelect={(ri) => setRis(ris.includes(ri) ? ris : ris.concat([ri]))}
 * />
 * ```
 */
export const Typeahead = <T,>({
  value,
  label,
  description,
  placeholder,
  prepend,
  append,
  errors,
  hasError,
  disabled,
  keyExtractor = JSON.stringify,
  renderOption,
  renderEmpty = defaultEmpty,
  hideEmpty = false,
  renderLoading = defaultLoading,
  seamlessLoading = false,
  onSearch,
  onSelect,
  popoverProps = {},
  renderValue = defaultRenderValue,
  onCreate,
  onInputChange,
  debounceDelay,
  className,
  showCreatedOption = false,
  updateSearchTextOnSelect = false,
  alwaysShowScrollbar = false,
  dropdownContainerStyle,
  dropdownItemStyle,
}: TypeaheadProps<T>) => {
  // Random ID for ARIA attributes
  const id = useRandomId();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [search, setSearch] = useState('');
  const { value: debouncedSearch } = useDebounce(search, debounceDelay);
  const popoverRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollingRef = useRef(false);
  const [createOption, setCreateOption] = useState<T | undefined>(undefined);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState<HTMLButtonElement | null>(
    null,
  );
  const [userCreatedOption, setUserCreatedOption] = useState<T | undefined>(
    undefined,
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  useEffect(() => {
    // If the mouse is hovering over the dropdown when keyboard nav is used to scroll out of view,
    // the onMouseEnter event is triggered during the scrolling operation and causes unexpected
    // behavior by jumping the selectedIndex back to wherever the mouse was
    scrollingRef.current = true;
    scrollChildIntoView(dropdownRef.current, selectedItem);

    // the mouse event happens after scrolling finishes, so slightly delay setting the ref to false
    setTimeout(() => {
      scrollingRef.current = false;
    }, 10);
  }, [selectedItem]);

  const performSearch = useCallback(
    async (search: string) => {
      const results = await onSearch(search);
      if (
        userCreatedOption &&
        showCreatedOption &&
        renderValue(userCreatedOption) === search
      ) {
        results.push(userCreatedOption);
      }
      setOptions(results);
      setLoading(false);
    },
    [renderValue, showCreatedOption, userCreatedOption, onSearch],
  );

  const onChange = (option: T) => {
    onSelect(option);

    if (updateSearchTextOnSelect) {
      setSearch(renderValue(option));
    }

    setOpen(false);
  };

  const onChooseUserOption = (option: T) => {
    onChange(option);
    setOptions(options.concat(option));
    setUserCreatedOption(option);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      setSelectedIndex(selectedIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      setSelectedIndex(selectedIndex - 1);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();

      if (options.length > 0) {
        const index = mod(selectedIndex, options.length);
        onChange(options[index]);
      } else if (createOption) {
        onChooseUserOption(createOption);
      }

      setOpen(false);
      (event.target as HTMLInputElement).blur();
    }
  };

  useEffect(() => {
    setLoading(true);
  }, [search]);

  useEffect(() => {
    if (updateSearchTextOnSelect) {
      setSearch(renderValue(value));
    }
  }, [value, renderValue, updateSearchTextOnSelect]);

  useEffect(() => {
    performSearch(debouncedSearch);
    if (onCreate) {
      setCreateOption(onCreate(debouncedSearch));
      if (!debouncedSearch) {
        setCreateOption(undefined);
      }
    }
  }, [onCreate, debouncedSearch, performSearch]);

  usePopoverFocus(
    inputRef,
    useCallback(() => setOpen(true), []),
    useCallback(() => setOpen(false), []),
  );

  const showPopover = () =>
    open &&
    !!search &&
    ((loading && !seamlessLoading) || options.length > 0 || !hideEmpty);

  // Show the create option if there are no other options
  const shouldShowCreateOption = options.length === 0;

  const inputContainerClasses = classNames(className, 'ds2-5-input--container');

  const inputWrapperClasses = classNames('ds2-5-input--wrapper', {
    'ds2-5-input--error': !disabled && hasError,
    'ds2-5-input--disabled': disabled,
  });

  const labelClasses = label
    ? classNames('ds2-5-form--label', {
        'ds2-5-form--label-disabled': disabled,
      })
    : undefined;

  const descriptionClasses = description
    ? classNames('ds2-5-form--desc', {
        'ds2-5-form--label-disabled': disabled,
      })
    : undefined;

  const dropdownClasses = classNames('ds2-5-dropdown-menu', {
    'always-visible-scrollbar': alwaysShowScrollbar,
  });

  return (
    <div className={inputContainerClasses} aria-disabled={disabled}>
      {label && (
        <label
          className={labelClasses}
          htmlFor={`${id}-input`}
          id={`${id}-label`}
        >
          {label}
        </label>
      )}
      {description && (
        <div className={descriptionClasses} id={`${id}-desc`}>
          {description}
        </div>
      )}

      <Popover
        popoverRef={popoverRef}
        open={showPopover()}
        mode={PopoverMode.Click}
        widthMode={PopoverWidthMode.Activator}
        closeOnClick={false}
        maxHeight={288}
        render={() => (
          <DropdownMenuStyleContext
            ref={dropdownRef}
            className={dropdownClasses}
            style={dropdownContainerStyle}
          >
            {loading && !seamlessLoading
              ? renderLoading(search)
              : options.length === 0
                ? renderEmpty(search)
                : options.map((option, index) => {
                    const selected =
                      mod(selectedIndex, options.length) === index;

                    return (
                      <ListItem
                        className="ds2-5-dropdown-item"
                        style={dropdownItemStyle}
                        key={keyExtractor(option)}
                        ref={(ref) => selected && setSelectedItem(ref)}
                        selected={selected}
                        onMouseEnter={() =>
                          !scrollingRef.current && setSelectedIndex(index)
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          onChange(option);
                        }}
                      >
                        {renderOption(option)}
                      </ListItem>
                    );
                  })}
            {createOption && shouldShowCreateOption && (
              <ListItem
                className="ds2-5-dropdown-item"
                key={keyExtractor(createOption)}
                ref={(ref) => setSelectedItem(ref)}
                selected={true}
                onClick={(event) => {
                  event.stopPropagation();
                  onChooseUserOption(createOption);
                }}
              >
                {t('design_system.typeahead.create_new')}{' '}
                {renderOption(createOption)}
              </ListItem>
            )}
          </DropdownMenuStyleContext>
        )}
        onChange={!disabled ? setOpen : undefined}
        {...popoverProps}
      >
        <div
          className={inputWrapperClasses}
          onClick={() => inputRef.current?.focus()}
        >
          {prepend}
          <input
            onKeyDown={handleKeyDown}
            ref={inputRef}
            id={`${id}-input`}
            aria-labelledby={label ? `${id}-label` : undefined}
            aria-describedby={description ? `${id}-desc` : undefined}
            aria-errormessage={
              !disabled && hasError ? `${id}-errors` : undefined
            }
            aria-invalid={!disabled && hasError}
            type="text"
            data-1p-ignore
            placeholder={placeholder}
            value={open ? search : (renderValue(value) ?? search)}
            disabled={disabled}
            onChange={(event) => {
              if (onInputChange) {
                onInputChange(event.target.value);
              }

              setSearch(event.target.value);
            }}
          />
          {append}
        </div>
      </Popover>

      {!disabled && hasError && <Errors id={`${id}-errors`} errors={errors} />}
    </div>
  );
};
